import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AuditLogsData } from '../../../features/administration-panel/audit-logs/models/AuditLogs';
import { AuditLogEnum } from 'src/app/features/administration-panel/audit-logs/models/enums/AuditLogEnum';
import { AuditLogService } from 'src/app/features/central-apps-hub/modules/applications/services/audit-log.service';

// Interface for dialog data that will be injected
export interface DialogData {
  record: AuditLogsData; // The audit log record data
  type: AuditLogEnum | string;          // Type of payload (RequestBody / Response)
  typeTitle: string      // Type Title
  isAuditLog: boolean
}

@Component({
  selector: 'app-log-details',
  standalone: false,
  templateUrl: './log-details.component.html'
})
export class LogDetailsComponent implements OnInit {
  dataLoading = false
  constructor(
    private snackBar: MatSnackBar, // For showing notifications
    @Inject(MAT_DIALOG_DATA) public data: DialogData, // Injected dialog data
    private readonly translate: TranslateService, // For translations
    private service: AuditLogService,
  ) { }

  logData: any; // Holds the parsed log data to be displayed

  ngOnInit(): void {
    // Decide which payload to parse based on the type
    if (!this.data.isAuditLog) {
      if (this.data.type == 'RequestBody') {
        this.logData = this.parseJsonSafe(this.data.record.requestPayload)
      } else if (this.data.type == 'Response') {
        this.logData = this.parseJsonSafe(this.data.record.responsePayload)
      }
    } else {
      this.getAuditLogRequestOrResponse()
    }
  }

  getAuditLogRequestOrResponse() {
    let params = {
      AuditLogEnum: this.data.type,
      logId: this.data.record.id
    }
    this.dataLoading = true
    this.service.getAuditLogRequestOrResponse(params).subscribe((data: any) => {
      this.dataLoading = false
      this.logData = this.parseJsonSafe(data)
    }, (error: any) => {
      this.dataLoading = false
    });
  }

  // Copy formatted log data to clipboard
  copyToClipboard() {
    navigator.clipboard.writeText(JSON.stringify(this.logData, null, 2));
    this.openSnackBar();
  }

  // Show a snack bar notification after copying
  openSnackBar() {
    this.snackBar.open(
      this.translate.instant('auditLogs.copiedToClipboard'), // Translated message
      '✖', // Close button
      {
        duration: 1000, // Duration in milliseconds
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      }
    );
  }

  // Safely parse JSON and handle cases where it's double-stringified
  parseJsonSafe(value: any): any {
    try {
      let parsed = JSON.parse(value);
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed); // Handle nested JSON string
      }
      return this.cleanQuotes(parsed);
    } catch {
      return value; // Return original value if parsing fails
    }
  }

  // Remove unnecessary quotes from strings inside objects/arrays
  cleanQuotes(obj: any): any {
    if (typeof obj === 'string') {
      return obj.replace(/^"|"$/g, ''); // Remove surrounding quotes
    } else if (Array.isArray(obj)) {
      return obj.map(item => this.cleanQuotes(item)); // Clean each array element
    } else if (typeof obj === 'object' && obj !== null) {
      const newObj: any = {};
      for (const key in obj) {
        newObj[key] = this.cleanQuotes(obj[key]); // Recursively clean object keys
      }
      return newObj;
    }
    return obj; // Return unchanged if not string/array/object
  }
}
